import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceDot
} from 'recharts';
import { 
  Radio, MessageSquare, Zap, UserPlus, Package, 
  GitBranch, Rocket, Clock, Cpu, Sparkles, Heart
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../services/api';

const POLL_INTERVAL = 10000; // 10 seconds

const COPILOT_LABELS = {
  executive: 'Executive',
  feature: 'Feature',
  epic: 'Epic',
  goal: 'Goal',
  release: 'Release',
  persona: 'Persona',
  wizard: 'Wizard',
  initiative: 'Initiative',
  kpi: 'KPI',
  collection: 'Collection',
};

const HEARTBEAT_ICONS = {
  user_signup: UserPlus,
  product_created: Package,
  feature_created: Zap,
  release_created: Rocket,
};

const HEARTBEAT_COLORS = {
  user_signup: 'text-emerald-400',
  product_created: 'text-blue-400',
  feature_created: 'text-sky-400',
  release_created: 'text-violet-400',
};

function timeAgo(isoString) {
  if (!isoString) return '';
  const diff = Date.now() - new Date(isoString).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ago`;
}

function durationLabel(isoString) {
  if (!isoString) return '';
  const diff = Date.now() - new Date(isoString).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return remMin > 0 ? `${hr}h ${remMin}m` : `${hr}h`;
}

// ─── Who's Here Panel ─────────────────────────────
const WhosHere = ({ users, colors }) => {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-4">
        <div className="relative">
          <Radio className="w-4 h-4 text-emerald-400" />
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-emerald-400 rounded-full animate-ping" />
        </div>
        <span className={cn("text-sm font-medium", colors.textSecondary)}>Who's Here</span>
      </div>

      {users.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <p className={cn("text-sm italic", colors.textMuted)}>
            Everyone's resting. You should too.
          </p>
        </div>
      ) : (
        <div className="space-y-1 overflow-y-auto flex-1 pr-1" data-testid="whos-here-list">
          {users.map((u, i) => (
            <div
              key={u.user_id || i}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-lg transition-all",
                "hover:bg-white/5",
                colors.bgTertiary
              )}
              style={{ animation: `fadeSlideIn 0.3s ease ${i * 60}ms both` }}
              data-testid={`active-user-${i}`}
            >
              <span className="relative flex-shrink-0">
                <span className="w-2 h-2 bg-emerald-400 rounded-full block" />
              </span>
              <div className="min-w-0 flex-1">
                <p className={cn("text-sm font-medium truncate", colors.textPrimary)}>
                  {u.name?.split(' ')[0] || 'Unknown'}
                  <span className={cn("font-normal ml-1", colors.textMuted)}>
                    @ {u.organization_name}
                  </span>
                </p>
                <p className={cn("text-xs truncate", colors.textMuted)}>
                  {COPILOT_LABELS[u.last_feature] || u.last_feature || 'browsing'}
                  {u.action_count > 0 && (
                    <span className="ml-1 opacity-60">
                      &middot; {u.action_count} action{u.action_count !== 1 ? 's' : ''}
                    </span>
                  )}
                </p>
              </div>
              <span className={cn("text-xs tabular-nums flex-shrink-0", colors.textMuted)}>
                {durationLabel(u.last_activity)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── AI Feed Panel ────────────────────────────────
const AiFeed = ({ feed, colors }) => {
  const listRef = useRef(null);
  const [prevCount, setPrevCount] = useState(0);

  useEffect(() => {
    // Auto-scroll only if new items appeared
    if (feed.length > prevCount && listRef.current) {
      listRef.current.scrollTop = 0;
    }
    setPrevCount(feed.length);
  }, [feed.length]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-4 h-4 text-amber-400" />
        <span className={cn("text-sm font-medium", colors.textSecondary)}>What They're Asking</span>
        {feed.length > 0 && (
          <Badge variant="secondary" className="text-xs h-5 px-1.5 bg-amber-500/10 text-amber-400 border-0">
            {feed.length}
          </Badge>
        )}
      </div>

      {feed.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <p className={cn("text-sm italic", colors.textMuted)}>
            No copilot conversations yet today...
          </p>
        </div>
      ) : (
        <div ref={listRef} className="space-y-2 overflow-y-auto flex-1 pr-1" data-testid="ai-feed-list">
          {feed.map((item, i) => (
            <div
              key={`${item.timestamp}-${i}`}
              className={cn(
                "px-3 py-2.5 rounded-lg transition-all",
                colors.bgTertiary
              )}
              style={{ animation: `fadeSlideIn 0.3s ease ${i * 40}ms both` }}
              data-testid={`ai-feed-item-${i}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={cn("text-xs font-medium", colors.textSecondary)}>
                  {item.user_name?.split(' ')[0] || 'Someone'}
                  <span className={cn("font-normal ml-1", colors.textMuted)}>
                    @ {item.organization_name}
                  </span>
                </span>
                <span className={cn("text-xs tabular-nums", colors.textMuted)}>
                  {timeAgo(item.timestamp)}
                </span>
              </div>
              {item.prompt_preview ? (
                <p className={cn("text-sm leading-relaxed", colors.textPrimary)}>
                  "{item.prompt_preview}"
                  {item.prompt_preview.length >= 200 && '...'}
                </p>
              ) : (
                <p className={cn("text-sm italic", colors.textMuted)}>
                  Used {COPILOT_LABELS[item.copilot_type] || item.feature || 'copilot'}
                </p>
              )}
              <div className="flex items-center gap-2 mt-1.5">
                <Badge variant="outline" className={cn("text-xs h-5 px-1.5 border-0", "bg-white/5", colors.textMuted)}>
                  <Cpu className="w-3 h-3 mr-1" />
                  {COPILOT_LABELS[item.copilot_type] || item.feature}
                </Badge>
                {item.tokens_used > 0 && (
                  <span className={cn("text-xs", colors.textMuted)}>
                    {item.tokens_used.toLocaleString()} tokens
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Heartbeat Panel ──────────────────────────────
const Heartbeat = ({ events, colors }) => {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-4">
        <GitBranch className="w-4 h-4 text-violet-400" />
        <span className={cn("text-sm font-medium", colors.textSecondary)}>Heartbeat</span>
      </div>

      {events.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <p className={cn("text-sm italic", colors.textMuted)}>
            Quiet right now...
          </p>
        </div>
      ) : (
        <div className="space-y-0.5 overflow-y-auto flex-1 pr-1" data-testid="heartbeat-list">
          {events.map((ev, i) => {
            const Icon = HEARTBEAT_ICONS[ev.type] || Zap;
            const color = HEARTBEAT_COLORS[ev.type] || 'text-gray-400';
            return (
              <div
                key={`${ev.timestamp}-${i}`}
                className="flex items-center gap-2 px-2 py-1.5 rounded"
                style={{ animation: `fadeSlideIn 0.3s ease ${i * 50}ms both` }}
                data-testid={`heartbeat-item-${i}`}
              >
                <Icon className={cn("w-3.5 h-3.5 flex-shrink-0", color)} />
                <span className={cn("text-xs truncate flex-1", colors.textSecondary)}>
                  {ev.label}
                </span>
                <span className={cn("text-xs tabular-nums flex-shrink-0 opacity-50", colors.textMuted)}>
                  {timeAgo(ev.timestamp)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Daily Pulse Chart ────────────────────────────
const HOUR_LABELS = ['12a','','','3a','','','6a','','','9a','','','12p','','','3p','','','6p','','','9p','',''];

const PulseTooltip = ({ active, payload, colors }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className={cn("px-3 py-2 rounded-lg border text-xs shadow-lg", colors.bgCard, colors.borderPrimary)}>
      <p className={cn("font-medium", colors.textPrimary)}>{d.label}</p>
      <p className={cn(colors.textMuted)}>
        {d.value} {d.value === 1 ? 'action' : 'actions'}
      </p>
    </div>
  );
};

const DailyPulse = ({ pulse, colors }) => {
  if (!pulse) return null;

  const { hours, activity, current_hour, peak_hour, peak_value, bpm } = pulse;
  const hasActivity = activity.some(v => v > 0);

  // Build chart data
  const chartData = hours.map((h, i) => {
    const ampm = h < 12 ? 'am' : 'pm';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return {
      hour: h,
      value: activity[i],
      label: `${h12}${ampm}`,
      isCurrent: h === current_hour,
      isPeak: h === peak_hour && peak_value > 0,
    };
  });

  const currentData = chartData.find(d => d.isCurrent);

  return (
    <Card
      className={cn("border p-4 relative overflow-hidden", colors.bgCard, colors.borderPrimary)}
      data-testid="daily-pulse-chart"
    >
      {/* BPM Badge */}
      <div className="absolute top-3 right-4 flex items-center gap-1.5 z-10">
        <Heart
          className="w-3.5 h-3.5 text-rose-400"
          style={{ animation: bpm > 0 ? 'bpmPulse 1s ease-in-out infinite' : 'none' }}
        />
        <span className={cn("text-sm font-mono font-bold tabular-nums", colors.textPrimary)}>
          {bpm}
        </span>
        <span className={cn("text-xs", colors.textMuted)}>bpm</span>
      </div>

      {/* Title */}
      <div className="flex items-center gap-2 mb-2">
        <span className={cn("text-sm font-medium", colors.textSecondary)}>Daily Pulse</span>
        {peak_value > 0 && (
          <span className={cn("text-xs", colors.textMuted)}>
            Peak: {chartData[peak_hour]?.label} — {peak_value} actions
          </span>
        )}
      </div>

      {hasActivity ? (
        <div style={{ height: 120 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="pulseGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#34d399" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#34d399" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="hour"
                tickFormatter={(h) => HOUR_LABELS[h] || ''}
                tick={{ fontSize: 10, fill: 'currentColor', opacity: 0.4 }}
                axisLine={false}
                tickLine={false}
                interval={0}
              />
              <YAxis hide />
              <Tooltip
                content={<PulseTooltip colors={colors} />}
                cursor={false}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#34d399"
                strokeWidth={2}
                fill="url(#pulseGradient)"
                animationDuration={1200}
                animationEasing="ease-out"
              />
              {/* Current hour dot */}
              {currentData && (
                <ReferenceDot
                  x={current_hour}
                  y={currentData.value}
                  r={5}
                  fill="#34d399"
                  stroke="none"
                  className="animate-ping-slow"
                />
              )}
              {/* Peak dot */}
              {peak_value > 0 && peak_hour !== current_hour && (
                <ReferenceDot
                  x={peak_hour}
                  y={peak_value}
                  r={3}
                  fill="#fbbf24"
                  stroke="none"
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex items-center justify-center" style={{ height: 120 }}>
          <div className="text-center">
            <div className={cn("text-sm italic", colors.textMuted)}>
              The day hasn't started yet. The first heartbeat is coming.
            </div>
            {/* Flat line with pulse */}
            <div className="mt-3 relative h-px w-48 mx-auto overflow-visible">
              <div className={cn("absolute inset-0", colors.borderPrimary)} style={{ height: 1 }} />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-emerald-400"
                style={{
                  right: 0,
                  animation: 'softPulse 2s ease-in-out infinite',
                  boxShadow: '0 0 8px rgba(52, 211, 153, 0.5)',
                }}
              />
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};


// ─── Main LiveTab Component ───────────────────────
const LiveTab = ({ colors }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const fetchPulse = useCallback(async () => {
    try {
      const res = await api.get('/api/admin/live/pulse?window_minutes=60');
      setData(res.data);
      setError(null);
    } catch (err) {
      console.error('Live pulse fetch failed:', err);
      setError('Connection lost');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPulse();
    intervalRef.current = setInterval(fetchPulse, POLL_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [fetchPulse]);

  const summary = data?.summary || {};
  const activeUsers = data?.active_users || [];
  const aiFeed = data?.ai_feed || [];
  const heartbeat = data?.heartbeat || [];
  const hourlyPulse = data?.hourly_pulse || null;

  const joySentence = summary.today_users > 0
    ? `Today, ${summary.today_users} ${summary.today_users === 1 ? 'person' : 'people'} across ${summary.today_orgs} ${summary.today_orgs === 1 ? 'organization' : 'organizations'} used StackRanked to build better products.`
    : "A new day is starting. The first person to log in will make this counter tick.";

  return (
    <div className="space-y-4" data-testid="live-tab">
      {/* CSS animations */}
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes softPulse {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
        @keyframes bpmPulse {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.25); opacity: 1; }
        }
        .animate-ping-slow {
          animation: pingGlow 2s ease-in-out infinite;
        }
        @keyframes pingGlow {
          0%, 100% { opacity: 0.7; r: 5; }
          50% { opacity: 1; r: 7; }
        }
      `}</style>

      {/* Joy Sentence */}
      <div
        className={cn(
          "text-center py-4 px-6 rounded-xl border",
          colors.bgCard, colors.borderPrimary
        )}
        data-testid="live-joy-sentence"
      >
        <p className={cn("text-base font-medium leading-relaxed", colors.textPrimary)}
           style={{ animation: 'softPulse 4s ease-in-out infinite' }}>
          {joySentence}
        </p>
        {summary.today_ai_calls > 0 && (
          <p className={cn("text-xs mt-1.5", colors.textMuted)}>
            {summary.today_ai_calls.toLocaleString()} AI conversations so far
          </p>
        )}
      </div>

      {/* Daily Pulse Chart */}
      <DailyPulse pulse={hourlyPulse} colors={colors} />

      {/* Three-panel layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" style={{ minHeight: '520px' }}>
        {/* Who's Here — left panel */}
        <Card
          className={cn(
            "lg:col-span-3 border p-4 flex flex-col",
            colors.bgCard, colors.borderPrimary
          )}
          data-testid="whos-here-panel"
        >
          <div className="flex items-center justify-between mb-1">
            <span className={cn("text-2xl font-bold tabular-nums", colors.textPrimary)}>
              {activeUsers.length}
            </span>
            <span className={cn("text-xs", colors.textMuted)}>
              {activeUsers.length === 1 ? 'person' : 'people'} building
            </span>
          </div>
          <WhosHere users={activeUsers} colors={colors} />
        </Card>

        {/* AI Feed — center panel */}
        <Card
          className={cn(
            "lg:col-span-6 border p-4 flex flex-col",
            colors.bgCard, colors.borderPrimary
          )}
          data-testid="ai-feed-panel"
        >
          <AiFeed feed={aiFeed} colors={colors} />
        </Card>

        {/* Heartbeat — right panel */}
        <Card
          className={cn(
            "lg:col-span-3 border p-4 flex flex-col",
            colors.bgCard, colors.borderPrimary
          )}
          data-testid="heartbeat-panel"
        >
          <Heartbeat events={heartbeat} colors={colors} />
        </Card>
      </div>

      {/* Connection indicator */}
      <div className="flex items-center justify-center gap-2 py-1">
        <span className={cn(
          "w-1.5 h-1.5 rounded-full",
          error ? "bg-red-400" : "bg-emerald-400"
        )} />
        <span className={cn("text-xs", colors.textMuted)}>
          {error ? 'Reconnecting...' : 'Live — refreshing every 10s'}
        </span>
      </div>
    </div>
  );
};

export default LiveTab;
